import React, {useEffect, useState, useContext, useRef, useLayoutEffect} from 'react';
import {Row, Col, Form, Button, Modal, InputGroup} from "react-bootstrap";
import TypeBar from "../components/TypeBar";
import ProductList from "../components/ProductList";
import {fetchCategories, fetchProducts, fetchStores, fetchSizes} from "../http/productAPI";
import Pages from "../components/Pages";
import { useLocation, useNavigate } from 'react-router-dom';
import { Context } from '../index';
import { FormControl } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';
import useModalCleanup from '../hooks/useModalCleanup';
import { getImageUrl } from '../utils/imageUtils';

const Shop = observer(() => {
    const { user } = useContext(Context);
    const [categories, setCategories] = useState([])
    const [stores, setStores] = useState([])
    const [products, setProducts] = useState([])
    const [totalCount, setTotalCount] = useState(0)
    const [page, setPage] = useState(1)
    const [limit] = useState(16)
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const gender = searchParams.get('gender');
    const type = searchParams.get('type');
    const [allSizes, setAllSizes] = useState([]);
    // === FIX: Инициализируем selectedSizes из URL при первом рендере ===
    const getInitialSizes = () => {
        try {
            const params = new URLSearchParams(location.search);
            const sizesParam = params.get('sizes');
            if (sizesParam) {
                const arr = JSON.parse(decodeURIComponent(sizesParam));
                if (Array.isArray(arr)) return arr;
            }
        } catch {}
        return [];
    };
    const [selectedSizes, setSelectedSizes] = useState(getInitialSizes);
    const [pendingSizes, setPendingSizes] = useState(getInitialSizes);
    const [showFilters, setShowFilters] = useState(false);
    const [sizeInput, setSizeInput] = useState('');
    const [showSizeSuggestions, setShowSizeSuggestions] = useState(false);
    const [filteredSizes, setFilteredSizes] = useState([]);
    // === FIX: Инициализируем filters из URL при первом рендере ===
    const getInitialFilters = () => {
        try {
            const params = new URLSearchParams(location.search);
            return {
                category_id: params.get('category_id') || '',
                store_id: params.get('store_id') || ''
            };
        } catch {
            return { category_id: '', store_id: '' };
        }
    };
    const [filters, setFilters] = useState(getInitialFilters);
    const [search, setSearch] = useState('');
    // Начальные значения сортировки
    const [productSortBy, setProductSortBy] = useState('price');
    const [productSortDirection, setProductSortDirection] = useState('desc');
    // === FIX: Флаг для отслеживания программных обновлений URL ===
    const isUpdatingURL = useRef(false);
    // === FIX: Отслеживание предыдущего type для предотвращения ложных сбросов ===
    const prevTypeRef = useRef(type);

    const [loadingProducts, setLoadingProducts] = useState(false);

    useModalCleanup(showFilters);

    // Теперь просто:
    const allCategories = categories;

    // Сброс выбранной категории при смене типа (только если type действительно изменился)
    // === FIX: Сбрасываем категорию только при реальном изменении type, не при возврате ===
    useEffect(() => {
        // Сбрасываем категорию только если type действительно изменился
        if (prevTypeRef.current !== type && prevTypeRef.current !== null && prevTypeRef.current !== undefined) {
            // Проверяем, нет ли сохраненной категории в URL
            const params = new URLSearchParams(location.search);
            const categoryIdFromURL = params.get('category_id') || '';
            // Сбрасываем только если в URL нет сохраненной категории
            if (!categoryIdFromURL) {
                setFilters(prev => ({ ...prev, category_id: '' }));
            }
        }
    }, [type, location.search]);

    // Сброс фильтров при смене типа (только если type действительно изменился)
    // === FIX: Этот useEffect должен выполняться ПОСЛЕ восстановления из URL ===
    useEffect(() => {
        // === FIX: Сбрасываем размеры только если type действительно изменился ===
        // И только если в URL нет сохраненных размеров (чтобы не сбросить при возврате)
        if (prevTypeRef.current !== type && prevTypeRef.current !== null && prevTypeRef.current !== undefined) {
            const params = new URLSearchParams(location.search);
            const sizesParam = params.get('sizes');
            // Сбрасываем только если в URL нет сохраненных размеров
            if (!sizesParam) {
                setSelectedSizes([]);
                setPendingSizes([]);
                setSizeInput('');
                setFilteredSizes([]);
                setShowSizeSuggestions(false);
            }
        }
        prevTypeRef.current = type;
    }, [type, location.search]);

    // Загрузка размеров в зависимости от категории
    useEffect(() => {
        const loadSizes = async () => {
            if (type === 'shoe') {
                const data = await fetchSizes('shoe');
                setAllSizes(data.filter(sz => sz.type === 'shoe'));
            } else if (type === 'clothes' && filters.category_id) {
                const categoryObj = categories.find(c => String(c.id) === String(filters.category_id));
                const catName = categoryObj?.name;
                let data;
                if (catName === 'Бюстгальтер') {
                    data = await fetchSizes('clothes', 'Бюстгальтер');
                    setAllSizes(data.filter(sz => sz.category === 'Бюстгальтер'));
                } else if (catName === 'Трусы') {
                    data = await fetchSizes('clothes', 'Трусы');
                    setAllSizes(data.filter(sz => sz.category === 'Трусы'));
                } else if (catName === 'Детская одежда') {
                    data = await fetchSizes('clothes', 'Детская одежда');
                    setAllSizes(data.filter(sz => sz.category === 'Детская одежда'));
                } else {
                    data = await fetchSizes('clothes');
                    setAllSizes(data.filter(sz => !sz.category));
                }
            } else {
                setAllSizes([]);
            }
        };
        loadSizes();
    }, [type, filters.category_id, categories]);

    // Фильтрация размеров при вводе
    useEffect(() => {
        if (sizeInput.trim() === '') {
            setFilteredSizes(allSizes); // Показываем все размеры, если поле пустое
            return;
        }
        const filtered = allSizes.filter(size => 
            size.value.toLowerCase().includes(sizeInput.toLowerCase())
        );
        setFilteredSizes(filtered);
    }, [sizeInput, allSizes]);

    // Обновляем список размеров при изменении allSizes
    useEffect(() => {
        setFilteredSizes(allSizes);
    }, [allSizes]);

    useEffect(() => {
        fetchCategories().then(data => {
            setCategories(data);
        });
        fetchStores().then(data => setStores(data));
    }, []);

    // === УДАЛЕНО: Дублирующий useEffect, который сбрасывал размеры ===
    // Этот useEffect был удален, так как он дублировал логику выше и мог сбрасывать размеры при возврате

    // --- Новый useEffect: сброс страницы при изменении поиска ---
    useEffect(() => {
        setPage(1);
    }, [search]);

    // --- Синхронизация состояния с URL ---
    // Восстановление состояния из URL при монтировании и при изменении location.search
    // === FIX: Используем useLayoutEffect для синхронного выполнения перед рендером ===
    useLayoutEffect(() => {
        // Пропускаем восстановление, если мы сами обновляем URL
        if (isUpdatingURL.current) {
            isUpdatingURL.current = false;
            return;
        }
        
        const params = new URLSearchParams(location.search);
        const pageParam = parseInt(params.get('page'));
        const searchParam = params.get('search') || '';
        const categoryId = params.get('category_id') || '';
        const storeId = params.get('store_id') || '';
        const sizesParam = params.get('sizes');
        const sortByParam = params.get('sort_by') || 'price';
        const sortDirParam = params.get('sort_dir') || (sortByParam.endsWith('_asc') ? 'asc' : 'desc');
        
        // Обновляем состояние только если оно отличается от текущего, чтобы избежать лишних ре-рендеров
        if (pageParam > 0 && pageParam !== page) {
            setPage(pageParam);
        } else if (!pageParam && page !== 1) {
            setPage(1);
        }
        
        if (searchParam !== search) {
            setSearch(searchParam);
        }
        
        if (categoryId !== filters.category_id || storeId !== filters.store_id) {
            setFilters({
                category_id: categoryId,
                store_id: storeId
            });
        }
        
        // === FIX: Восстанавливаем размеры из URL ===
        if (sizesParam) {
            try {
                const arr = JSON.parse(decodeURIComponent(sizesParam));
                if (Array.isArray(arr)) {
                    // Всегда обновляем, если в URL есть параметр sizes
                    const currentSizesStr = JSON.stringify(selectedSizes);
                    const newSizesStr = JSON.stringify(arr);
                    if (currentSizesStr !== newSizesStr) {
                        console.log('[Shop] Восстанавливаем размеры из URL:', arr);
                        setSelectedSizes(arr);
                        setPendingSizes(arr); // Также обновляем pendingSizes для синхронизации
                    }
                }
            } catch (e) {
                console.error('Ошибка при парсинге sizes из URL:', e);
            }
        } else {
            // === FIX: НЕ сбрасываем размеры автоматически, если их нет в URL ===
            // Это предотвратит сброс при возврате с ProductPage, если параметры не были сохранены
            // Размеры будут сброшены только при явном изменении type (см. useEffect выше)
        }
        
        if (sortByParam !== productSortBy) {
            setProductSortBy(sortByParam);
        }
        
        if (sortDirParam !== productSortDirection) {
            setProductSortDirection(sortDirParam);
        }
    }, [location.search]); // === FIX: Слушаем изменения location.search для восстановления при возврате ===

    // Обновление URL при изменении фильтров, страницы, поиска, размеров, сортировки
    useEffect(() => {
        // === FIX: Устанавливаем флаг перед обновлением URL ===
        isUpdatingURL.current = true;
        
        const params = new URLSearchParams(location.search);
        params.set('page', page);
        params.set('search', search);
        if (filters.category_id) params.set('category_id', filters.category_id);
        else params.delete('category_id');
        if (filters.store_id) params.set('store_id', filters.store_id);
        else params.delete('store_id');
        if (selectedSizes.length > 0) params.set('sizes', encodeURIComponent(JSON.stringify(selectedSizes)));
        else params.delete('sizes');
        params.set('sort_by', productSortBy);
        params.set('sort_dir', productSortDirection);
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }, [page, search, filters.category_id, filters.store_id, selectedSizes, productSortBy, productSortDirection]);

    useEffect(() => {
        const loadProducts = async () => {
            try {
                setLoadingProducts(true);
                const storeId = filters.store_id || null;
                const categoryId = filters.category_id || null;
                // --- Формируем параметры запроса (отдаём пагинацию на backend) ---
                const params = {
                    categoryId,
                    limit: 100000,
                    page: 1,
                    gender,
                    type,
                    store_id: storeId,
                    ...(selectedSizes && selectedSizes.length > 0 && (type === 'shoe' || type === 'clothes')
                        ? { 'size[]': selectedSizes }
                        : {})
                };
                // --- Добавляем search только если не пустой ---
                if (search && search.trim() !== '') {
                    params.search = search;
                }

                const data = await fetchProducts(params);
                let rows = data.rows || [];
                // --- Сортировка ДО пагинации (по ВСЕМ товарам), чтобы работала на всех страницах ---
                if (productSortBy === 'quantity') {
                    rows = [...rows].sort((a, b) => {
                        const qa = getProductQuantity(a);
                        const qb = getProductQuantity(b);
                        return productSortDirection === 'asc' ? qa - qb : qb - qa;
                    });
                } else if (productSortBy === 'price') {
                    rows = [...rows].sort((a, b) => {
                        const pa = a.price || 0;
                        const pb = b.price || 0;
                        return productSortDirection === 'asc' ? pa - pb : pb - pa;
                    });
                } else if (productSortBy === 'rating') {
                    rows = [...rows].sort((a, b) => {
                        const ra = typeof a.averageRating === 'number' ? a.averageRating : 0;
                        const rb = typeof b.averageRating === 'number' ? b.averageRating : 0;
                        return productSortDirection === 'asc' ? ra - rb : rb - ra;
                    });
                } else if (productSortBy === 'createdAt') {
                    rows = [...rows].sort((a, b) => {
                        const da = new Date(a.createdAt).getTime() || 0;
                        const db = new Date(b.createdAt).getTime() || 0;
                        return productSortDirection === 'asc' ? da - db : db - da;
                    });
                }

                // --- Пагинация ПОСЛЕ сортировки: режем данные на страницы на фронте ---
                    const startIdx = (page - 1) * limit;
                    const endIdx = startIdx + limit;
                    setProducts(rows.slice(startIdx, endIdx));
                setTotalCount(rows.length);
            } catch (error) {
                console.error('Error fetching products:', error);
            } finally {
                setLoadingProducts(false);
            }
        };

        loadProducts();
    }, [filters.category_id, filters.store_id, page, limit, gender, type, selectedSizes, user?.store_id, search, productSortBy, productSortDirection]);

    useEffect(() => {
        document.title = "Каталог товаров";
    }, []);

    const handleSizeSelect = (size) => {
        if (!pendingSizes.includes(size.value)) {
            setPendingSizes([...pendingSizes, size.value]);
        }
        setSizeInput('');
        setShowSizeSuggestions(false);
    };

    const handleRemoveSize = (sizeToRemove) => {
        setPendingSizes(pendingSizes.filter(size => size !== sizeToRemove));
    };

    // Фильтрация категорий по типу товара
    const filteredCategories = React.useMemo(() => {
        if (!type) return categories;
        return categories.filter(cat => cat.type === type);
    }, [categories, type]);

    // --- Функция для получения количества товара (копия из AdminPanel.js/ProductList.js) ---
    function getProductQuantity(product) {
        if (product.type === 'toy') {
            return product.toy?.quantity ?? product.stock ?? 0;
        } else if (product.type === 'shoe' || product.type === 'clothes') {
            return (product.variants || []).reduce((sum, v) => sum + (Number(v.quantity) || 0), 0);
        }
        return 0;
    }

    // Функция для смены сортировки из ProductList
    const handleSortChange = (newSortBy, newSortOrder) => {
        setProductSortBy(newSortBy);
        setProductSortDirection(newSortOrder);
        setPage(1); // сбрасываем на первую страницу при смене сортировки
    };

    // Старт поиска по нажатию кнопки/Enter
    const handleSearchSubmit = (value) => {
        setSearch(value || '');
        setPage(1);
    };

    // Сброс всех фильтров и поиска
    const handleResetFilters = () => {
        setFilters({ category_id: '', store_id: '' });
        setSelectedSizes([]);
        setPendingSizes([]);
        setSizeInput('');
        setSearch('');
        setPage(1);
    };

    const activeCategory = filters.category_id
        ? categories.find(c => String(c.id) === String(filters.category_id)) || null
        : null;
    const activeStore = filters.store_id
        ? stores.find(s => String(s.id) === String(filters.store_id)) || null
        : null;
    const hasActiveFilters =
        !!activeCategory || !!activeStore || (Array.isArray(selectedSizes) && selectedSizes.length > 0);

    return (
        <>
            <Modal
                show={showFilters}
                onHide={() => setShowFilters(false)}
                centered
                backdrop="static"
                animation={false}
                style={{ pointerEvents: 'auto' }}
                dialogClassName="filter-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Фильтры</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                    {/* Магазин */}
                    <Form.Group className="mb-3">
                        <Form.Label>Магазин</Form.Label>
                        <Form.Select 
                            value={filters.store_id || ''} 
                            onChange={e => {
                                const store = stores.find(s => String(s.id) === e.target.value);
                                setFilters(prev => ({ ...prev, store_id: store ? store.id : '' }));
                            }}
                        >
                            <option value="">Все магазины</option>
                            {stores.map(store => (
                                <option key={store.id} value={store.id}>{store.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {/* Размеры для обуви и одежды */}
                    {(type === 'shoe' || (type === 'clothes' && filters.category_id)) && allSizes.length > 0 && (
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Размеры {(() => {
                                    const cat = categories.find(c => String(c.id) === String(filters.category_id));
                                    return cat ? `(${cat.name})` : '';
                                })()}
                            </Form.Label>
                            <div className="position-relative">
                                <InputGroup>
                                    <FormControl
                                        type="text"
                                        placeholder="Введите размер..."
                                        value={sizeInput}
                                        onChange={(e) => {
                                            setSizeInput(e.target.value);
                                        }}
                                        onClick={() => {
                                            setShowSizeSuggestions(true);
                                            setFilteredSizes(allSizes);
                                        }}
                                        onFocus={() => {
                                            setShowSizeSuggestions(true);
                                            setFilteredSizes(allSizes);
                                        }}
                                    />
                                    <Button 
                                        variant="outline-secondary"
                                        onClick={() => {
                                            setSizeInput('');
                                            setFilteredSizes(allSizes);
                                        }}
                                    >
                                        ✕
                                    </Button>
                                </InputGroup>
                                
                                {/* Выпадающий список размеров */}
                                {showSizeSuggestions && filteredSizes.length > 0 && (
                                    <div 
                                        className="position-absolute w-100 bg-white border rounded mt-1 shadow-sm"
                                        style={{ 
                                            zIndex: 1000, 
                                            maxHeight: '200px', 
                                            overflowY: 'auto',
                                            pointerEvents: 'auto'
                                        }}
                                    >
                                        {filteredSizes.map(size => (
                                            <div
                                                key={size.id}
                                                className="p-2 hover-bg-light cursor-pointer"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleSizeSelect(size)}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                            >
                                                {size.value}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Выбранные размеры */}
                                {pendingSizes.length > 0 && (
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                        {pendingSizes.map(size => (
                                            <div 
                                                key={size}
                                                className="bg-light rounded-pill px-3 py-1 d-flex align-items-center"
                                            >
                                                <span>{size}</span>
                                                <Button
                                                    variant="link"
                                                    className="p-0 ms-2 text-danger"
                                                    onClick={() => handleRemoveSize(size)}
                                                    style={{ fontSize: '0.8rem' }}
                                                >
                                                    ✕
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Form.Group>
                    )}

                    {/* Категории */}
                    <Form.Group className="mb-3">
                        <Form.Label>Категория</Form.Label>
                        <Form.Select
                            name="category_id"
                            value={filters.category_id}
                            onChange={e => setFilters(prev => ({ ...prev, category_id: e.target.value }))}
                        >
                            <option value="">Все категории</option>
                            {filteredCategories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setPendingSizes(selectedSizes); // Отмена - сброс к уже примененным
                        setShowFilters(false)
                    }}>
                        Отмена
                    </Button>
                    <Button variant="primary" onClick={() => {
                        setSelectedSizes(pendingSizes); // Применяем выбранные размеры
                        setShowFilters(false)
                    }}>
                        Применить
                    </Button>
                </Modal.Footer>
            </Modal>
            
            <Row>
                <Col md={3} className="d-none d-md-block">
                    <TypeBar
                        categories={allCategories}
                        selectedCategory={categories.find(c => String(c.id) === String(filters.category_id)) || null}
                        setSelectedCategory={category => setFilters(prev => ({ ...prev, category_id: category.id }))}
                        selectedType={type}
                    />
                </Col>
                <Col xs={12} md={9}>
                    <ProductList
                        products={products}
                        page={page}
                        setPage={setPage}
                        totalCount={totalCount}
                        limit={limit}
                        setShowFilters={setShowFilters}
                        search={search}
                        setSearch={setSearch}
                        sortBy={productSortBy}
                        sortOrder={productSortDirection}
                        onSortChange={handleSortChange}
                        onSearchSubmit={handleSearchSubmit}
                        isSearching={loadingProducts && !!search}
                        activeFilters={{
                            category: activeCategory,
                            store: activeStore,
                            sizes: selectedSizes
                        }}
                        hasActiveFilters={hasActiveFilters}
                        onResetFilters={handleResetFilters}
                    />
                    <div style={{ marginBottom: 32, marginTop: 16, display: 'flex', justifyContent: 'center', width: '100%' }} aria-label="Пагинация">
                        <Pages totalCount={totalCount} page={page} setPage={setPage} limit={limit} />
                    </div>
                </Col>
            </Row>
        </>
    );
});

export default Shop;